import React, { useState } from 'react';
import { ArrowLeft, MapPin, Phone, User, CreditCard, Truck, CheckCircle, ChevronRight } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

interface CheckoutPageProps {
  onBack: () => void;
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({ onBack }) => {
  const { cartItems, getTotalPrice, clearCart } = useCart();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'wechat' | 'alipay' | 'card'>('wechat');
  const [isProcessing, setIsProcessing] = useState(false);

  // 模拟收货地址
  const defaultAddress = {
    name: '张三',
    phone: '138****8888',
    address: '广东省深圳市南山区科技园南区科苑路科兴科学园B栋3单元',
    isDefault: true
  };

  // 模拟配送方式
  const deliveryOptions = [
    { id: 'standard', name: '标准配送', time: '1-3个工作日', price: 0, selected: true },
    { id: 'express', name: '快速配送', time: '当日达', price: 15, selected: false },
    { id: 'premium', name: '特快配送', time: '2小时内', price: 25, selected: false }
  ];

  // 模拟支付方式
  const paymentMethods = [
    { id: 'wechat', name: '微信支付', icon: '💚', selected: true },
    { id: 'alipay', name: '支付宝', icon: '💙', selected: false },
    { id: 'card', name: '银行卡', icon: '💳', selected: false }
  ];

  const subtotal = getTotalPrice();
  const deliveryFee = deliveryOptions.find(opt => opt.selected)?.price || 0;
  const total = subtotal + deliveryFee;

  const handleCheckout = async () => {
    setIsProcessing(true);
    
    // 模拟支付处理
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // 清空购物车
    clearCart();
    
    // 这里可以跳转到支付成功页面或订单确认页面
    alert('订单提交成功！');
    onBack();
  };

  const renderOrderSummary = () => (
    <div className="bg-white rounded-xl p-4 mb-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">订单商品</h3>
      <div className="space-y-3">
        {cartItems.map((item) => (
          <div key={item.id} className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
              {item.image.startsWith('/') ? (
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              {/* 备用显示 */}
              <div className={`w-full h-full flex items-center justify-center text-lg ${item.image.startsWith('/') ? 'hidden' : ''}`}>
                🍽️
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-800 text-sm line-clamp-2">
                {item.name}
              </h4>
              <p className="text-sm text-gray-500">数量: {item.quantity}</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-gray-800">¥{(item.price * item.quantity).toFixed(2)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDeliveryAddress = () => (
    <div className="bg-white rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">收货地址</h3>
        <button className="text-primary text-sm">修改</button>
      </div>
      <div className="border border-gray-200 rounded-lg p-3">
        <div className="flex items-start space-x-3">
          <MapPin size={20} className="text-gray-400 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className="font-medium text-gray-800">{defaultAddress.name}</span>
              <span className="text-gray-500">{defaultAddress.phone}</span>
              {defaultAddress.isDefault && (
                <span className="px-2 py-1 bg-primary text-white text-xs rounded-full">默认</span>
              )}
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">{defaultAddress.address}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDeliveryOptions = () => (
    <div className="bg-white rounded-xl p-4 mb-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">配送方式</h3>
      <div className="space-y-3">
        {deliveryOptions.map((option) => (
          <label key={option.id} className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="delivery"
              checked={option.selected}
              onChange={() => {}}
              className="text-primary focus:ring-primary"
            />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-800">{option.name}</span>
                <span className="text-gray-500 text-sm">{option.time}</span>
              </div>
            </div>
            <span className="text-gray-800 font-medium">
              {option.price === 0 ? '免费' : `¥${option.price}`}
            </span>
          </label>
        ))}
      </div>
    </div>
  );

  const renderPaymentMethods = () => (
    <div className="bg-white rounded-xl p-4 mb-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">支付方式</h3>
      <div className="space-y-3">
        {paymentMethods.map((method) => (
          <label key={method.id} className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="payment"
              checked={selectedPaymentMethod === method.id}
              onChange={() => setSelectedPaymentMethod(method.id as any)}
              className="text-primary focus:ring-primary"
            />
            <div className="flex items-center space-x-2">
              <span className="text-xl">{method.icon}</span>
              <span className="font-medium text-gray-800">{method.name}</span>
            </div>
          </label>
        ))}
      </div>
    </div>
  );

  const renderOrderTotal = () => (
    <div className="bg-white rounded-xl p-4 mb-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">订单总结</h3>
      <div className="space-y-3">
        <div className="flex justify-between text-gray-600">
          <span>商品小计</span>
          <span>¥{subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-gray-600">
          <span>配送费</span>
          <span>{deliveryFee === 0 ? '免费' : `¥${deliveryFee}`}</span>
        </div>
        <div className="border-t pt-3">
          <div className="flex justify-between text-lg font-bold">
            <span>总计</span>
            <span className="text-red-600">¥{total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* 头部导航 */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="flex items-center justify-between p-4">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h2 className="text-lg font-semibold text-gray-800">确认订单</h2>
          <div className="w-10"></div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* 订单商品 */}
        {renderOrderSummary()}
        
        {/* 收货地址 */}
        {renderDeliveryAddress()}
        
        {/* 配送方式 */}
        {renderDeliveryOptions()}
        
        {/* 支付方式 */}
        {renderPaymentMethods()}
        
        {/* 订单总结 */}
        {renderOrderTotal()}
      </div>

      {/* 底部结算按钮 */}
      <div className="fixed bottom-20 left-0 right-0 p-4 bg-white border-t">
        <button
          onClick={handleCheckout}
          disabled={isProcessing || cartItems.length === 0}
          className="w-full bg-primary text-white py-4 rounded-lg font-medium text-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>处理中...</span>
            </>
          ) : (
            <>
              <CheckCircle size={20} />
              <span>提交订单 ¥{total.toFixed(2)}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CheckoutPage;
